import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowUpRight } from "lucide-react";
import { Seo, SITE_URL, breadcrumbJsonLd } from "../components/Seo";
import { useI18n } from "../i18n/LanguageContext";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, c } = useI18n();
  const p = t.post;
  const post = c.BLOG.find((b) => b.slug === slug);
  const others = c.BLOG.filter((b) => b.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col items-center justify-center text-center pt-28" data-testid="post-not-found">
        <h1 className="font-display text-4xl font-semibold text-navy">{p.notFound}</h1>
        <Link to="/blog" className="btn-primary mt-6">{p.backBtn}</Link>
      </div>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.image,
    datePublished: post.date,
    articleSection: post.category,
    author: { "@type": "Organization", name: "OffplanDubai.nl" },
    publisher: { "@type": "Organization", name: "OffplanDubai.nl", logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.jpg` } },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
    description: post.excerpt,
  };

  return (
    <div className="pt-28 sm:pt-32" data-testid="blog-post">
      <Seo path={`/blog/${post.slug}`} title={post.title} description={post.excerpt} image={post.image} type="article"
        jsonLd={[articleLd, breadcrumbJsonLd([{ name: t.crumbHome, path: "/" }, { name: t.blog.crumb, path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }])]} />
      <article className="container-x max-w-3xl">
        <Link to="/blog" data-testid="post-back" className="inline-flex items-center gap-2 text-sm font-medium text-muted transition-colors hover:text-gold"><ArrowLeft size={16} /> {p.back}</Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="rounded-full bg-gold/10 px-3 py-1 text-xs font-medium text-gold">{post.category}</span>
          <span className="flex items-center gap-1 text-xs text-muted"><Clock size={13} /> {post.read}</span>
          <span className="text-xs text-muted">· {post.date}</span>
        </div>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-navy sm:text-5xl">{post.title}</h1>
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="mt-8 overflow-hidden rounded-[2rem]">
          <img src={post.image} alt={post.title} className="h-[280px] w-full object-cover sm:h-[420px]" />
        </motion.div>
        <div className="mt-10 space-y-6">
          {post.body.map((para, i) => <p key={i} className="text-lg leading-relaxed text-navy/80">{para}</p>)}
        </div>
        <div className="my-14 rounded-3xl bg-navy p-8 text-center sm:p-10">
          <h3 className="font-display text-2xl font-semibold text-white">{p.ctaTitle}</h3>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/70">{p.ctaSub}</p>
          <Link to="/contact" className="btn-primary mt-6">{p.ctaBtn} <ArrowUpRight size={16} /></Link>
        </div>
      </article>
      <section className="container-x pb-24">
        <h2 className="font-display text-2xl font-semibold text-navy">{p.keepReading}</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {others.map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="group flex overflow-hidden rounded-3xl bg-white shadow-soft transition-transform duration-500 hover:-translate-y-1">
              <img src={b.image} alt={b.title} className="h-full w-32 shrink-0 object-cover" />
              <div className="p-5"><span className="text-xs font-medium text-gold">{b.category}</span><h3 className="mt-1 font-display text-lg font-semibold leading-snug text-navy">{b.title}</h3></div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
